/**
 * The shared runtime bits of personalisation: config URLs, config fetching, webfont registration
 * and text measurement (MFYEB-100).
 *
 * WHY THIS EXISTS. The same two URLs were being constructed in five places across three files,
 * every one of them assuming the shop metafield ends with a slash:
 *
 *   personalisation-widget.js          fetchFromCDN / fetchFromPIM
 *   personalisation-widget.js          the cart fee-sync loop
 *   nosto-personalized-cards-popup.js  fetchFromCDN / fetchFromPIM
 *
 * `custom.personalisation_cdn_endpoint_url` and `..._pim_endpoint_url` are typed by hand in
 * Shopify admin, and today they both end with a slash. Someone removing it would silently break
 * ALL personalisation on every one of those paths at once. The opposite mistake already cost us:
 * the preview snippet appended `/configs/...` to a base that already ended in a slash, producing
 * a double slash, and S3 behind CloudFront treats that as a different key and answers 403, so
 * every preview config request failed.
 *
 * So: normalise the base here, once, and let the callers ask for a URL rather than build one.
 *
 * NOT USED BY LIQUID for the URL part. Liquid needs the CDN URL before any JS runs and cannot
 * call this, so `snippets/personalisation-config-url.liquid` normalises the same way. A change to
 * the shape of these URLs has to touch both.
 *
 * WHY THE FONT AND MEASUREMENT HELPERS LIVE HERE TOO. Two components draw personalised text: the
 * product page's preview and the cart line's. They had their own copies of the same three things,
 * and one of the copies was actively wrong: each kept its OWN font cache, so a customer who had
 * already loaded a face on the product page had it registered a second time by the cart, under the
 * same family name. One cache per page is both less code and less work at runtime.
 *
 * What is deliberately NOT shared is the product page's config lifecycle. That looks like a
 * duplicate of `fetchConfig` and is not: it distinguishes a non-2xx from a parse failure from a
 * network error so it can hand the product to the legacy widget with a specific reason, and it
 * retries a load that was lost rather than finished. The cart has no such need - it either draws or
 * leaves the product photo alone - so giving both the same fetch would mean giving the cart a
 * lifecycle it does not want, or taking the reasons away from the page that does.
 */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api; // node (tests)
  }
  root.PimConfig = api; // browser
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // URLs
  // ---------------------------------------------------------------------------

  /** Exactly one trailing slash, whichever way the hand-entered value is written. */
  function base(value) {
    const s = String(value == null ? '' : value).trim();
    if (!s) return '';
    return s.endsWith('/') ? s : s + '/';
  }

  /** The published config file for a product: `{cdn}configs/product-<id>.json`. */
  function cdnUrl(cdnBase, shopifyProductId) {
    const b = base(cdnBase);
    if (!b || !shopifyProductId) return null;
    return b + 'configs/product-' + encodeURIComponent(shopifyProductId) + '.json';
  }

  /**
   * The live PIM endpoint for a product. Returns the ARRAY-shaped payload, not the CDN file's
   * object shape, which is why the preview cannot read it (see personalisation-preview.js).
   */
  function pimUrl(pimBase, shopifyProductId, currency) {
    const b = base(pimBase);
    if (!b || !shopifyProductId) return null;
    const q = currency ? '?currency=' + encodeURIComponent(currency) : '';
    return b + 'personalisation/' + encodeURIComponent(shopifyProductId) + q;
  }

  // ---------------------------------------------------------------------------
  // Config fetching, for consumers that just want the data
  // ---------------------------------------------------------------------------

  /**
   * How long any config request may take before it is given up on.
   *
   * The same 3 seconds the legacy widget allows its PIM call, so the two UIs wait the same amount
   * of time before falling back. Long enough for a cold CloudFront edge, short enough that a
   * customer is not left looking at a spinner.
   */
  const CONFIG_TIMEOUT_MS = 3000;

  /**
   * `fetch` with a deadline that actually ABORTS the request.
   *
   * WHY THIS EXISTS. Both config fetches used a bare `fetch`, so a CDN that accepted the
   * connection and then never answered left the promise pending forever. Not hypothetical, and the
   * consequence on the product page was the bad one: the modal's own load is what installs the
   * add-to-cart guard and what decides to fall back to the legacy form, so a hung request did
   * neither. The customer saw a spinner, no form appeared, and a product whose personalisation is
   * REQUIRED could still be added to the bag with no name on it.
   *
   * A rejection is recoverable and a hang is not, which is the whole point: every caller here
   * already handles a failed fetch, so turning a hang into a failure is enough to fix it.
   *
   * It aborts rather than just stopping waiting (which is what the legacy widget's
   * `personalisationWithDeadline` does), so a slow response does not keep a connection open.
   */
  function fetchWithDeadline(url, options, timeoutMs) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs || CONFIG_TIMEOUT_MS);

    return fetch(url, Object.assign({}, options || {}, { signal: controller.signal })).finally(
      () => clearTimeout(timer)
    );
  }

  /** url -> Promise<config|null>, so N cart lines of one product make ONE request. */
  const configs = new Map();

  /**
   * Fetch a published config once per URL per page.
   *
   * Resolves to null on any failure, which is all a simple consumer needs: it either has data to
   * draw or it leaves what was already on screen alone. Callers that need to know WHY a config did
   * not load should not use this - see the note in the file header.
   *
   * SUCCESS IS CACHED, FAILURE IS NOT, and the asymmetry is the point. Caching the promise is what
   * makes N cart lines of one product share a single request. But this used to cache the FAILURE
   * too: one timeout or one 502 resolved to null, that null stayed in the map, and every later
   * caller got it back without a request. So a single blip left the cart drawing no previews at all
   * until the customer reloaded the page, and adding the deadline above made that more likely
   * rather than less, because a stall now settles as a failure instead of hanging.
   *
   * Dropping the entry on failure means the next caller retries. The retries are bounded by the
   * number of callers, not by time, so a dead CDN costs one request per cart line and then stops.
   * `personalisationFetchJsonOnce` in personalisation-widget.js does the same thing for the legacy
   * widget's fetches; the two now behave alike.
   */
  function fetchConfig(url) {
    if (!url) return Promise.resolve(null);

    if (!configs.has(url)) {
      configs.set(
        url,
        fetchWithDeadline(url, { cache: 'no-store' })
          .then((res) => (res.ok ? res.json() : null))
          .then((config) => {
            // A non-2xx resolves to null above; treat that as a failure too, so a 404 for a
            // product whose config has not published yet is retried rather than remembered.
            if (config === null) configs.delete(url);
            return config;
          })
          .catch(() => {
            configs.delete(url);
            return null;
          })
      );
    }

    return configs.get(url);
  }

  // ---------------------------------------------------------------------------
  // Fonts
  // ---------------------------------------------------------------------------

  /**
   * CSS family for a PIM font code.
   *
   * Must match what the existing widget uses: a font registered under one name is not reachable
   * under another, and both UIs load from the same PIM font set.
   */
  function fontFamilyFor(code) {
    return code ? 'pim-' + code : '';
  }

  /** family -> Promise<boolean>. One per page, shared by every component that draws text. */
  const fonts = new Map();

  /**
   * Register a PIM font as a FontFace, once per page.
   *
   * Resolves false when there is no file to load or the load fails. Callers should treat that as
   * "do not draw": falling back to the browser's default serif would show letterforms the workshop
   * will never produce, which is worse than showing no preview.
   */
  /**
   * How long a webfont may take before it is given up on.
   *
   * Longer than the config's deadline because a font file is an order of magnitude bigger than a
   * JSON config, and the two are sequential on the product page, so this is the second half of the
   * worst case a customer can wait. Both being bounded is the point; neither used to be.
   */
  const FONT_TIMEOUT_MS = 5000;

  /**
   * Resolve false rather than hang for ever if a webfont never arrives.
   *
   * NOT CANCELLABLE, unlike `fetchWithDeadline` above: `FontFace.load()` takes no signal, so the
   * request carries on in the background and this only stops waiting on it. That is the honest
   * limit of what the API allows, and it is enough, because the damage was never the open
   * connection.
   *
   * The damage was upstream. The product page awaits every font before it marks itself ready, and
   * marking itself ready is what installs the add-to-cart guard and what decides to hand over to
   * the legacy form. So one font file that accepted the connection and never answered left the
   * customer on a spinner with NEITHER a guard nor a fallback, and a product whose personalisation
   * is required could be bought with no name on it. Exactly the hole the config fetch had.
   */
  function loadFaceWithDeadline(face, timeoutMs) {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(
        () => reject(new Error('font load timed out after ' + timeoutMs + 'ms')),
        timeoutMs
      );
      face.load().then(
        (loaded) => {
          clearTimeout(timer);
          resolve(loaded);
        },
        (error) => {
          clearTimeout(timer);
          reject(error);
        }
      );
    });
  }

  /**
   * @param choice    a font entry from the config's `options.fonts`
   * @param timeoutMs optional override, so a test can prove the deadline is wired in without
   *                  waiting five real seconds for it
   */
  function registerFont(choice, timeoutMs) {
    const code = choice && choice.value;
    const url = choice && choice.font_file_url;
    if (!code || !url) return Promise.resolve(false);

    const family = fontFamilyFor(code);

    if (!fonts.has(family)) {
      fonts.set(
        family,
        (async () => {
          try {
            const face = new FontFace(family, 'url(' + url + ')', { display: 'swap' });
            await loadFaceWithDeadline(face, timeoutMs || FONT_TIMEOUT_MS);
            document.fonts.add(face);
            return true;
          } catch (e) {
            // Warned rather than thrown: one missing file must not stop the others loading.
            // CALLERS MUST HONOUR THE FALSE. Drawing anyway shows the customer their name in the
            // browser's default serif, which is letterforms the workshop will never stitch. The
            // cart line preview refuses on it; the product page ignored it, and that was a bug.
            if (typeof console !== 'undefined' && console.warn) {
              console.warn('[personalisation] font failed: ' + code, e);
            }
            return false;
          }
        })()
      );
    }

    return fonts.get(family);
  }

  /** How far ahead of the viewport the options may be when their fonts start loading. */
  const FONT_ROOT_MARGIN = '300px 0px';

  const fontWaits = new WeakMap();

  /**
   * Resolve once the shopper is about to see the personalisation options in `el`: the page has
   * finished loading and `el` is near the viewport, or the shopper points at or tabs into it.
   * Keeps webfonts out of the product page load (MFS-639).
   *
   * @param env optional `{ win, doc, IntersectionObserver }`, for tests
   */
  function whenFontsWanted(el, env) {
    if (!el || typeof el.addEventListener !== 'function') return Promise.resolve();
    if (fontWaits.has(el)) return fontWaits.get(el);

    const win = (env && env.win) || (typeof window !== 'undefined' ? window : null);
    const doc = (env && env.doc) || (typeof document !== 'undefined' ? document : null);
    const Observer = env
      ? env.IntersectionObserver
      : typeof IntersectionObserver !== 'undefined'
        ? IntersectionObserver
        : null;
    const intents = ['pointerenter', 'focusin'];

    const wait = new Promise((resolve) => {
      let done = false;
      let observer = null;

      const start = () => {
        if (done) return;
        done = true;
        if (observer) observer.disconnect();
        intents.forEach((name) => el.removeEventListener(name, start));
        resolve();
      };

      const observe = () => {
        if (done) return;
        if (!Observer) return start();
        observer = new Observer(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) start();
          },
          { rootMargin: FONT_ROOT_MARGIN }
        );
        observer.observe(el);
      };

      intents.forEach((name) => el.addEventListener(name, start));

      if (!doc || !win || doc.readyState === 'complete') observe();
      else win.addEventListener('load', observe, { once: true });
    });

    fontWaits.set(el, wait);
    return wait;
  }

  // ---------------------------------------------------------------------------
  // Text measurement
  // ---------------------------------------------------------------------------

  /**
   * Canvas measurement, shared through one context per page.
   *
   * The geometry module asks its caller for these two numbers rather than measuring anything
   * itself, which is what lets it be unit-tested in node and reused by Engine's admin editor.
   *
   * `capHeight` measures the ink height of a capital X specifically: no descender, no overshoot
   * from a round letter, no swash. That is what makes two different fonts render at the same
   * visual size.
   */
  let ctx = null;

  function measureContext() {
    if (!ctx) ctx = document.createElement('canvas').getContext('2d');
    return ctx;
  }

  function measureWidth(text, sizeUserUnits, family) {
    const c = measureContext();
    c.font = sizeUserUnits + 'px "' + family + '"';
    return c.measureText(text).width;
  }

  function measureCapHeight(sizeUserUnits, family) {
    const c = measureContext();
    c.font = sizeUserUnits + 'px "' + family + '"';
    const m = c.measureText('X');
    return m && m.actualBoundingBoxAscent ? m.actualBoundingBoxAscent : 0;
  }

  /**
   * How far a row of ordinary text reaches above plus below its baseline, measured on the
   * geometry's INK_SAMPLE. Sets the gap between the rows of a multi-line message, so a font with
   * short capitals is not drawn double-spaced and a loopy script does not collide (MFYEB-212).
   */
  function measureInkHeight(sizeUserUnits, family) {
    const sample = (window.PimPreviewGeometry && window.PimPreviewGeometry.INK_SAMPLE) || 'Hbdfhklgjpqy';
    const c = measureContext();
    c.font = sizeUserUnits + 'px "' + family + '"';
    const m = c.measureText(sample);
    const ink = m ? (m.actualBoundingBoxAscent || 0) + (m.actualBoundingBoxDescent || 0) : 0;
    return ink > 0 ? ink : 0;
  }

  // MFYEB-195: toUpperCase (not locale-aware) matches the server's mb_strtoupper. The limit is
  // re-applied because a script-set value ignores maxlength and some letters grow (ß -> SS).
  function toUppercaseValue(value, limit) {
    const next = String(value || '').toUpperCase();
    return limit && next.length > limit ? next.slice(0, limit) : next;
  }

  function applyUppercase(target, limit, isComposing) {
    const value = String(target.value || '');
    if (isComposing) return value;

    const next = toUppercaseValue(value, limit);
    if (next === value) return value;

    const start = typeof target.selectionStart === 'number' ? target.selectionStart : null;
    target.value = next;
    if (start !== null && typeof target.setSelectionRange === 'function') {
      const caret = Math.min(value.slice(0, start).toUpperCase().length, next.length);
      target.setSelectionRange(caret, caret);
    }
    return next;
  }

  return {
    base,
    cdnUrl,
    pimUrl,
    CONFIG_TIMEOUT_MS,
    FONT_TIMEOUT_MS,
    fetchWithDeadline,
    // Exported for its unit test: the deadline is the whole point of it, and 5 real seconds is
    // not something a test suite should wait for.
    loadFaceWithDeadline,
    fetchConfig,
    fontFamilyFor,
    registerFont,
    whenFontsWanted,
    measureWidth,
    measureCapHeight,
    measureInkHeight,
    applyUppercase,
    toUppercaseValue,
  };
});
